<template>
	<div
		class="time-picker"
		:class="{
			'is-disabled': disabled,
			'has-error': !isValid
		}"
	>
		<Label
			class="time-picker__label"
			:labelText="labelText"
			v-if="labelText"
		/>

		<div class="time-picker__input-container">
			<input
				class="time-picker__input"
				:id="uid"
				v-model="dataValue"
				:placeholder="placeholder"
				type="text"
				autocomplete="off"
				ref="timePickerOpts"
				data-testid="inputTimer"
				@keyup.esc="setExpandedOnFalse()"
				@keyup.enter="addIconeFocus"
			/>

			<i class="icon icon-error-solid" />

			<i
				ref="iconTimePicker"
				class="icon icon-time"
				@click="setExpanded(true)"
				data-testid="iconBtn"
				@keyup.enter="setExpanded(true)"
				@keyup.esc="setExpandedOnFalse()"
			/>
		</div>

		<TimePickerOptions
			ref="menu"
			class="time-picker__menu"
			v-show="isOpen"
			v-model="objParsedValue"
			:type="timePickerType"
			:isOpen="isOpen"
			@close="setExpandedOnFalse"
		/>

		<div class="time-picker__text-error" v-if="!isValid">
			{{ errorMessage }}
		</div>
		<HelpFeedback
			:helpText="helpText"
			v-if="helpText"
		/>

		<PopperPosition
			trackedRefName="timePickerOpts"
			popoverRefName="menu"
			placement="bottom-start"
			:popperVisible="isOpen"
			strategy="fixed"
		/>
	</div>
</template>
<script>
import Label from '@/components/forms/label/label';
import TimePickerOptions from '@/components/forms/time-picker/time-picker-options';
import DocumentClickedMixin from '@/mixins/document-click';
import PopperPosition from '@/components/visual/popper-position';
import HelpFeedback from '@/components/forms/help-feedback/help-feedback';
import { TIME_FULL } from './time-picker-types';
import { set, addMinutes, format } from 'date-fns';

export default {
	name: 'TimePicker',
	mixins: [DocumentClickedMixin('setExpandedOnFalse')],
	components: {
		Label,
		TimePickerOptions,
		PopperPosition,
		HelpFeedback
	},
	props: {
		placeholder: {
			type: String,
			default: undefined
		},
		value: {
			type: String,
			default: undefined
		},
		labelText: {
			type: String,
			default: null
		},
		timePickerType: {
			type: String,
			default: TIME_FULL
		},
		helpText: {
			type: String,
			default: ''
		}
	},
	data() {
		return {
			dataValue: '',
			objParsedValue: {
				hours: '00',
				minutes: '00',
				cyclePeriod: 'am'
			},
			isOpen: false,
			isValid: false,
			regex: /^(0[0-9]|1[0-2]):[0-5][0-9] [ap]m|[AP]M$/,
			errorMessage: 'Invalid format, use HH:MM AM/PM'
		};
	},
	methods: {
		parseValue(val) {
			let hours = val.trim().split(':')[0];
			let splittedValue = val
				.trim()
				.split(':')[1]
				.split(' ');

			this.objParsedValue = {
				hours: hours,
				minutes: splittedValue[0],
				cyclePeriod: splittedValue[1].toLowerCase()
			};
		},
		setExpanded(val) {
			this.isOpen = val;
		},
		setExpandedOnFalse() {
			this.isOpen = false;
		},
		updatedTime(value) {
			this.objParsedValue = { ...value };
		},
		emitValue(val) {
			this.$emit('change', val);
			this.$emit('input', val);
			let utcTimer = this.convertToUTC2(val);
			this.$emit('utctime', utcTimer);
		},
		convertToUTC2(timeString) {
			// Step 1: Get the local time in 24-hour format
			const [hora, minutes, periodo] = timeString.split(/:| /);
			let hours = parseInt(hora, 10);
			if (periodo.toLowerCase() === 'pm') {
				hours += 12;
			}
			const localDateTime = new Date();

			// Step 2: Set the provided hours and minutes
			const modifiedLocalDateTime = set(localDateTime, {
				hours,
				minutes
			});

			// Step 3: Convert to UTC by subtracting the time zone offset
			const utcDateTime = addMinutes(
				modifiedLocalDateTime,
				-modifiedLocalDateTime.getTimezoneOffset()
			);

			// Step 4: Format the UTC time in the desired format
			const desiredFormat = "HH:mm'Z'";
			const utcTimeFormatted = format(utcDateTime, desiredFormat);

			return utcTimeFormatted;
		},
		validateInput(val) {
			this.isValid = this.regex.test(val);
		},
		addIconeFocus() {
			const iconElement = this.$refs.iconTimePicker;
			iconElement.setAttribute('tabindex', '0');
			iconElement.focus();
		}
	},
	watch: {
		value: {
			immediate: true,
			handler(val) {
				this.validateInput(val);
				this.dataValue = val;
			}
		},
		dataValue: {
			handler(val) {
				this.validateInput(val);
				if (this.isValid) {
					this.parseValue(val);
				}
			}
		},
		objParsedValue: {
			deep: true,
			handler(val) {
				const newTime = `${val.hours}:${val.minutes} ${val.cyclePeriod}`;
				this.validateInput(newTime);
				this.dataValue = newTime;
				if (this.isValid) {
					this.emitValue(newTime);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
@import '@/scss/5-objects/_form.scss';
.time-picker {
	position: relative;
	width: 188px;
	display: inline-flex;
	flex-direction: column;

	&__input-container {
		position: relative;
		display: flex;
		@include textfield;

		&:focus-within {
			-o-outline: $focus-outline;
			-moz-outline: $focus-outline;
			outline: $focus-outline;
			outline-offset: $focus-offset;
		}
	}

	&__input {
		cursor: pointer;
		display: inline-block;
		appearance: none;
		outline: none;
		border: none;
		width: 100%;
		background-color: $white;
		font-size: $input-font-size;
		padding: $input-padding-y 42px $input-padding-y $input-padding-x;
		border-radius: $border-radius;
		@include input-height;

		& ~ .icon {
			cursor: pointer;
			position: relative;
			display: inline-block;
			@include horizontal-center-with-transform;
			right: 0;
			padding: $input-padding-y $input-padding-x;
			font-size: $default-font-size;
			border-left: $form-input-border;
		}

		& ~ .icon-error-solid {
			display: none;
			right: 32px;
			border: none;
			color: $ui-negative;
		}
	}

	&__menu {
		z-index: 20;
	}

	&.has-error {
		.time-picker {
			&__input-container {
				-o-outline: $focus-outline;
				-moz-outline: $focus-outline;
				outline: $focus-outline;
				outline-offset: $focus-offset;
				outline-color: $ui-negative;
			}

			&__input {
				padding-right: 64px;
			}

			&__text-error {
				display: block;
				color: $ui-negative;
				font-size: $label-font-size - 2;
				margin-top: $gutter-sm;
			}
		}

		.icon-time {
			&::after {
				position: absolute;
				left: -1px;
				top: 0;
				display: inline-flex;
				content: '';
				height: 100%;
				width: 2px;
				background: $ui-negative;
			}
		}

		.icon-error-solid {
			display: inline-block;
		}
	}
}
</style>
